import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Radio } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import FeatureGuard from '@components/layout/feature-guard.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import { useRealtimeEditorContext } from '@utils/providers/realtime-editor-context.tsx';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { LiveSessionStatus } from '@utils/types/editors/live-transcript.ts';
import {
  readSessionSource,
  SessionSource
} from '@utils/editors/live-session-source.ts';
import { liveEditorPath } from '@utils/api/realtime/live-transcript.ts';
import LiveTranscriptEditor from '@pages/editors/live-transcript.tsx';
import RealtimeTranscriptionPanel from './realtime-transcription-panel.tsx';
import { RealtimeSourceForm } from './realtime-source-form.tsx';
import { RealtimeMediaPreview } from './realtime-media-preview.tsx';
import { useRealtimeFreshSession } from './use-realtime-fresh-session.tsx';
import './realtime-workspace.css';

export type LiveWorkspaceState = {
  sessionId: string;
  scope: string;
  ended: boolean;
  dirty: boolean;
  label: string;
  owner: LiveSessionStatus | null;
  saving: boolean;
  discard: () => boolean;
};
export const RealtimeWorkspace = () => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [params] = useSearchParams();
  const legacy = useParams().sessionId;
  const sessionId = params.get('session') || legacy || '';
  const navigate = useNavigate();
  const realtime = useRealtimeContext();
  const editor = useRealtimeEditorContext();
  const identity = TokenExtension.get().decoded;
  const identityScope = `${identity?.oid}:${identity?.uid}`;
  const [source, setSource] = useState<SessionSource | null>(() =>
    sessionId ? readSessionSource(sessionId) : null
  );
  const [tools, setTools] = useState<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<LiveWorkspaceState>({
    sessionId: '',
    scope: '',
    ended: false,
    dirty: false,
    label: '',
    owner: null,
    saving: false,
    discard: () => true
  });
  const [freshEpoch, setFreshEpoch] = useState(0);
  const reportState = useCallback(
    (next: LiveWorkspaceState) =>
      setStatus(previous =>
        previous.ended === next.ended &&
        previous.sessionId === next.sessionId &&
        previous.scope === next.scope &&
        previous.dirty === next.dirty &&
        previous.label === next.label &&
        previous.owner === next.owner &&
        previous.saving === next.saving &&
        previous.discard === next.discard
          ? previous
          : next
      ),
    []
  );
  useEffect(() => {
    if (legacy) navigate(liveEditorPath(legacy), { replace: true });
  }, [legacy, navigate]);
  useEffect(() => {
    if (sessionId) setSource(readSessionSource(sessionId));
  }, [sessionId, identityScope]);
  const reset = useCallback(() => {
    setSource(null);
    setFreshEpoch(epoch => epoch + 1);
    setStatus({
      sessionId: '',
      scope: '',
      ended: false,
      dirty: false,
      label: '',
      owner: null,
      saving: false,
      discard: () => true
    });
  }, []);
  const fresh = useRealtimeFreshSession({
    sessionId,
    identityScope,
    live: status,
    onFresh: reset
  });
  const currentStatusSaving =
    status.sessionId === sessionId &&
    status.scope === `${identityScope}:${sessionId}` &&
    status.saving;
  const theme = {
    '--rt-canvas': getColour('baseLayer'),
    '--rt-surface': getColour('secondLayer'),
    '--rt-border': getColour('border'),
    '--rt-text': getColour('normalText'),
    '--rt-muted': getColour('inactiveText'),
    '--rt-accent': getColour('activeText')
  } as CSSProperties;
  const sourceForm = (
    <RealtimeSourceForm
      key={`${sessionId || 'setup'}:${freshEpoch}`}
      sessionId={sessionId}
      source={source}
      onSource={setSource}
      onNew={fresh.open}
      freshDisabled={
        fresh.busy ||
        currentStatusSaving ||
        realtime.connectionState === 'connecting'
      }
    />
  );
  return (
    <main className="realtime-workspace" style={theme}>
      <header className="realtime-workspace-heading">
        <Button
          variant="outline"
          size="small"
          aria-label={t('back')}
          icon={<ArrowLeft size={16} />}
          onClick={() => navigate('/')}
        />
        <div className="realtime-workspace-title">
          <Radio size={21} aria-hidden />
          <h1>{t('realtime-editor')}</h1>
        </div>
        <span className="realtime-workspace-status" role="status">
          {sessionId
            ? status.label || t('loading')
            : t(realtime.connectionState)}
        </span>
      </header>
      <div className="realtime-workspace-grid">
        <aside
          className="realtime-workspace-sidebar"
          aria-label={t('connection-settings')}
        >
          {sessionId ? (
            sourceForm
          ) : (
            <FeatureGuard feature="realtime">{sourceForm}</FeatureGuard>
          )}
          <RealtimeMediaPreview
            url={source?.streamUrl || ''}
            active={!!sessionId || realtime.connectionState === 'connected'}
          />
          <div className="realtime-workspace-tools" ref={setTools} />
        </aside>
        <section
          className="realtime-workspace-transcript"
          aria-label={t('transcript')}
        >
          {sessionId ? (
            <LiveTranscriptEditor
              key={`${sessionId}:${freshEpoch}`}
              sessionId={sessionId}
              embedded
              toolsSlot={tools}
              onStateChange={reportState}
              freshResetBusy={fresh.busy}
            />
          ) : (
            <div className="realtime-setup-transcript">
              <div className="realtime-setup-title">
                <h2>{t('transcript')}</h2>
                <span>{t('live-source-ready-help')}</span>
                {source?.audioSource === 'microphone' && (
                  <div className="flex gap-2">
                    <Button
                      size="small"
                      variant={
                        editor.activeTab === 'partials' ? 'primary' : 'outline'
                      }
                      label={t('live-stream')}
                      onClick={() => editor.setActiveTab('partials')}
                    />
                    <Button
                      size="small"
                      variant={
                        editor.activeTab === 'finals' ? 'primary' : 'outline'
                      }
                      label={t('transcript')}
                      onClick={() => editor.setActiveTab('finals')}
                    />
                  </div>
                )}
              </div>
              <RealtimeTranscriptionPanel />
            </div>
          )}
        </section>
      </div>
      {fresh.dialog}
    </main>
  );
};
